import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import PromptCard from '@/components/PromptCard';
import { Bookmark } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Bookmarks() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    axios.get(`${API}/bookmarks`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setPrompts(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [user, token, navigate]);

  const handleVoteChange = (promptId, data) => {
    setPrompts(prev => prev.map(p =>
      p.id === promptId ? { ...p, upvotes: data.score, user_vote: data.vote } : p
    ));
  };

  const handleBookmarkChange = (promptId, data) => {
    if (!data.bookmarked) {
      setPrompts(prev => prev.filter(p => p.id !== promptId));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="bookmarks-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2 flex items-center gap-3">
          <Bookmark className="w-8 h-8 text-primary" /> Saved Prompts
        </h1>
        <p className="text-muted-foreground">Your bookmarked prompts collection</p>
      </div>

      {loading ? (
        <div className="columns-1 md:columns-2 lg:columns-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-border bg-card p-5 mb-4 break-inside-avoid animate-pulse">
              <div className="flex gap-2 mb-3"><div className="h-5 w-16 bg-muted rounded-full" /><div className="h-5 w-20 bg-muted rounded-full" /></div>
              <div className="h-5 w-3/4 bg-muted rounded mb-2" />
              <div className="h-4 w-full bg-muted rounded mb-1" />
              <div className="h-8 w-full bg-muted rounded mt-4" />
            </div>
          ))}
        </div>
      ) : prompts.length === 0 ? (
        <div className="text-center py-20" data-testid="no-bookmarks">
          <Bookmark className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No saved prompts yet</h3>
          <p className="text-sm text-muted-foreground">Bookmark prompts to build your personal collection.</p>
        </div>
      ) : (
        <div className="columns-1 md:columns-2 lg:columns-3 gap-4" data-testid="bookmarks-grid">
          {prompts.map(prompt => (
            <PromptCard key={prompt.id} prompt={prompt} onVoteChange={handleVoteChange} onBookmarkChange={handleBookmarkChange} />
          ))}
        </div>
      )}
    </div>
  );
}
